/** The map screen: filters sidebar, the graph itself, and the detail panel. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ApiError,
  api,
  levelColor,
  type Branch,
  type Graph,
  type GraphEdge,
  type KanjiNode,
  type Level,
} from '../api'
import BottomSheet from '../components/BottomSheet'
import DetailPanel from '../components/DetailPanel'
import KanjiMap from '../components/KanjiMap'
import MapControls from '../components/MapControls'
import { ErrorNote, Spinner } from '../components/ui'
import { useAuth } from '../store/auth'

export default function MapPage() {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()

  const level = (params.get('level') as Level | null) ?? user?.target_level ?? 'N5'
  const branch = params.get('branch')
  const selected = params.get('sel')
  const focus = params.get('focus')

  const [depth, setDepth] = useState(2)
  const [includeStrokes, setIncludeStrokes] = useState(false)
  // Whether selecting a character may pull in kanji from other JLPT levels.
  const [showOffLevel, setShowOffLevel] = useState(true)
  const [graph, setGraph] = useState<Graph | null>(null)
  const [branches, setBranches] = useState<Branch[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const [term, setTerm] = useState('')
  const [results, setResults] = useState<KanjiNode[]>([])
  const [searching, setSearching] = useState(false)
  // Below lg the controls live in a sheet rather than a permanent sidebar; the
  // map is the point of the screen and deserves all of a phone's width.
  const [controlsOpen, setControlsOpen] = useState(false)

  const setParam = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(params)
      for (const [k, v] of Object.entries(patch)) {
        if (v === null) next.delete(k)
        else next.set(k, v)
      }
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    api
      .graph({ level, depth, branch, include_strokes: includeStrokes })
      .then((g) => alive && setGraph(g))
      .catch((e) => alive && setError(e instanceof ApiError ? e.message : 'could not load the map'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [level, depth, branch, includeStrokes, reloadKey])

  useEffect(() => {
    let alive = true
    api
      .branches(level, 80)
      .then((b) => alive && setBranches(b))
      .catch(() => alive && setBranches([]))
    return () => {
      alive = false
    }
  }, [level])

  /**
   * Selecting a character pulls in every kanji built from it, ignoring the level
   * filter, and merges those into the displayed graph. Off-level arrivals are drawn
   * greyed out by KanjiMap rather than hidden, so the learner can see that 亻 turns
   * up in 101 characters without having to change level to find them.
   */
  // char -> the kanji built from it. Accumulates, so opening 舌 then 言 keeps both
  // branches on the map instead of replacing one with the other.
  const [families, setFamilies] = useState<Map<string, KanjiNode[]>>(new Map())
  const [loadingFamily, setLoadingFamily] = useState<Set<string>>(new Set())
  // Characters we have already fetched. The map needs this to stop offering an
  // expand arrow: the stored child_count includes single-stroke parts that the
  // graph endpoints deliberately filter out, so counts alone never settle.
  const [explored, setExplored] = useState<Set<string>>(new Set())
  const inFlight = useRef<Set<string>>(new Set())

  /**
   * Load the kanji that use `char`, ignoring the level filter.
   *
   * Only this direction is fetched. A character's own components sit to its *left*
   * in the layout, so merging them would create new roots and shift the whole column
   * vertically - and the detail panel already lists them under "Built from", which is
   * the better place to read a breakdown.
   */
  const loadFamily = useCallback(async (char: string) => {
    if (inFlight.current.has(char)) return
    inFlight.current.add(char)
    setLoadingFamily((prev) => new Set(prev).add(char))
    try {
      // `level: null` is the whole point here - no level restriction.
      const users = await api.usedIn(char, null, 400)
      setFamilies((prev) => new Map(prev).set(char, users))
      setExplored((prev) => new Set(prev).add(char))
    } catch {
      // Nothing landed - let the arrow be clicked again.
      inFlight.current.delete(char)
    } finally {
      setLoadingFamily((prev) => {
        const next = new Set(prev)
        next.delete(char)
        return next
      })
    }
  }, [])

  // Changing the filter invalidates the overlay: those nodes were merged relative to
  // a different base graph, and keeping them would leave orphans behind.
  useEffect(() => {
    setFamilies(new Map())
    setExplored(new Set())
    inFlight.current = new Set()
  }, [level, branch, depth, includeStrokes])

  useEffect(() => {
    if (selected) void loadFamily(selected)
  }, [selected, loadFamily])

  /**
   * Base graph plus the selected character's cross-level family.
   *
   * `dimmed` is every character the overlay added, whatever its level. Anything the
   * base graph already contained stays at full brightness: those are the level's own
   * kanji and the structural components underneath them, which belong there. The
   * family fan-out also surfaces intermediate component forms (selecting 口 reaches
   * 吾, 咼, 㕣) and those are noise at full contrast, so "came from the overlay" is
   * the right thing to grey out rather than "has a different level".
   */
  const { merged, dimmed, pinned } = useMemo<{
    merged: Graph | null
    dimmed: ReadonlySet<string>
    pinned: ReadonlyMap<string, string>
  }>(() => {
    const empty = { dimmed: new Set<string>(), pinned: new Map<string, string>() }
    if (!graph) return { merged: null, ...empty }
    if (families.size === 0) return { merged: graph, ...empty }

    const known = new Set(graph.nodes.map((n) => n.char))
    const seenEdge = new Set(graph.edges.map((e) => `${e.source}>${e.target}`))
    const extraNodes: KanjiNode[] = []
    const extraEdges: GraphEdge[] = []
    const pin = new Map<string, string>()

    for (const [parent, users] of families) {
      for (const n of users) {
        // With cross-level display off, a family member is only merged if it belongs
        // to the level in view. Components (level null) are structural, not
        // "another level", so they always come in.
        if (!showOffLevel && n.level !== null && n.level !== level) continue
        if (!known.has(n.char)) {
          known.add(n.char)
          extraNodes.push(n)
          // Remember where it came from so a later fetch cannot re-parent it.
          pin.set(n.char, parent)
        }
        // `parent` is a component *of* n, matching the API's parent->child form
        const key = `${n.char}>${parent}`
        if (!seenEdge.has(key)) {
          seenEdge.add(key)
          extraEdges.push({ source: n.char, target: parent, position: 0 })
        }
      }
    }

    if (extraNodes.length === 0 && extraEdges.length === 0) {
      return { merged: graph, ...empty }
    }

    return {
      merged: {
        ...graph,
        nodes: [...graph.nodes, ...extraNodes],
        edges: [...graph.edges, ...extraEdges],
      },
      dimmed: new Set(extraNodes.map((n) => n.char)),
      pinned: pin,
    }
  }, [graph, families, showOffLevel, level])

  // For the caption: of the characters brought in for the selection, how many are
  // real kanji from another level rather than intermediate component forms.
  const selectedFamily = selected ? families.get(selected) : undefined
  const offLevelCount = useMemo(
    () =>
      selectedFamily
        ? selectedFamily.filter((n) => dimmed.has(n.char) && n.level !== null).length
        : 0,
    [selectedFamily, dimmed],
  )

  // Only the filter identity resets the map's collapse state and viewport; merging
  // a family must not.
  const viewKey = `${level}|${branch ?? ''}|${depth}|${includeStrokes}`

  // Debounced search, so typing does not fire a request per keystroke.
  useEffect(() => {
    const q = term.trim()
    if (!q) {
      setResults([])
      return
    }
    setSearching(true)
    const t = setTimeout(() => {
      api
        .search(q, 20)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setSearching(false))
    }, 250)
    return () => clearTimeout(t)
  }, [term])

  const stats = useMemo(() => {
    if (!graph) return null
    const byLevel = new Map<string, number>()
    let radical = 0
    let radicalAndKanji = 0
    let componentOnly = 0
    let mastered = 0
    for (const n of graph.nodes) {
      if (n.level) byLevel.set(n.level, (byLevel.get(n.level) ?? 0) + 1)
      if (n.is_radical && n.is_kanji) radicalAndKanji++
      else if (n.is_radical) radical++
      else componentOnly++
      if (n.status === 'mastered') mastered++
    }
    return { byLevel, radical, radicalAndKanji, componentOnly, mastered }
  }, [graph])

  const openChar = (char: string) => setParam({ sel: char, focus: null })

  // One object, spread into both renderings, so the sidebar and the sheet can
  // never be given different props.
  const controlsProps = {
    term,
    onTerm: setTerm,
    searching,
    results,
    onPick: (char: string) => {
      setParam({ sel: char, focus: char })
      setControlsOpen(false)
    },
    level,
    onLevel: (v: string) => setParam({ level: v, branch: null }),
    branch,
    branches,
    onBranch: (v: string | null) => setParam({ branch: v }),
    depth,
    onDepth: setDepth,
    includeStrokes,
    onIncludeStrokes: setIncludeStrokes,
    showOffLevel,
    onShowOffLevel: setShowOffLevel,
    graph,
    stats,
    signedIn: Boolean(user),
  }

  return (
    <div className="flex h-full min-h-0 gap-3 p-3">
      {/* The controls are one component rendered in two places: a permanent
          sidebar from lg up, and a sheet over the map below it. */}
      <div className="hidden w-64 shrink-0 flex-col gap-3 overflow-y-auto lg:flex">
        <MapControls {...controlsProps} />
      </div>

      {/* ---------------------------------------------------------------- map */}
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {error && <ErrorNote>{error}</ErrorNote>}
        {graph?.truncated && (
          <div className="rounded-xl border border-n1/40 bg-n1/10 px-3 py-1.5 text-xs text-n1">
            This map was capped for performance. Narrow it with a branch filter or a lower depth.
          </div>
        )}
        <div className="relative min-h-0 flex-1">
          {loading && !merged && (
            <div className="grid h-full place-items-center rounded-2xl border border-ink-700/70">
              <Spinner label="Building the map…" />
            </div>
          )}
          {merged && (
            <>
              <KanjiMap
                graph={merged}
                selected={selected}
                focus={focus}
                viewKey={viewKey}
                dimmed={dimmed}
                loadingFamily={loadingFamily}
                pinned={pinned}
                explored={explored}
                onExpandRequest={(c) => void loadFamily(c)}
                onSelect={openChar}
              />
              {(loading || loadingFamily.size > 0) && (
                <div className="absolute left-1/2 top-3 -translate-x-1/2 rounded-lg bg-ink-950/90 px-3 py-1 text-xs text-ink-400">
                  {loadingFamily.size > 0 ? 'finding related kanji…' : 'updating…'}
                </div>
              )}
              {selected && selectedFamily && selectedFamily.length > 0 && (
                <div className="absolute bottom-3 left-1/2 hidden -translate-x-1/2 rounded-lg border border-ink-700 bg-ink-950/90 px-3 py-1 text-[11px] text-ink-400 sm:block">
                  <span className="jp text-slate-200">{selected}</span> is used in{' '}
                  {selectedFamily.length} characters
                  {offLevelCount > 0 && (
                    <span className="text-ink-500"> · {offLevelCount} outside {level}, shown dimmed</span>
                  )}
                </div>
              )}

              {/* Opens the controls the sidebar holds on desktop. Bottom-left so
                  it clears the map's own zoom cluster at top-right and stays
                  within thumb reach; the level is on the face of it so the
                  current filter is legible without opening anything. */}
              <button
                type="button"
                onClick={() => setControlsOpen(true)}
                className="absolute bottom-3 left-3 flex min-h-[44px] items-center gap-2 rounded-xl
                           border border-ink-700 bg-ink-900/95 pl-3 pr-3.5 text-xs font-semibold
                           text-slate-200 shadow-panel backdrop-blur active:bg-ink-800 lg:hidden"
                aria-label="Open search and filters"
              >
                <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                  <path d="M4 6h16M7 12h10M10 18h4" />
                </svg>
                Filters
                <span
                  className="rounded-full px-1.5 py-px text-[10px] font-bold"
                  style={{ background: `${levelColor[level]}22`, color: levelColor[level] }}
                >
                  {level}
                </span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------- detail */}
      {selected && (
        <div className="hidden w-[380px] shrink-0 xl:block">
          <DetailPanel
            char={selected}
            signedIn={Boolean(user)}
            activeLevel={level}
            onClose={() => setParam({ sel: null })}
            onNavigate={(c) => setParam({ sel: c, focus: c })}
            onChanged={() => setReloadKey((k) => k + 1)}
          />
        </div>
      )}
      {/* Below xl the breakdown arrives as a bottom sheet instead of a side
          panel: a 380px panel on a 375px screen is the whole screen anyway, and
          anchoring it to the bottom keeps the close button in thumb reach. */}
      {selected && (
        <BottomSheet
          open
          onClose={() => setParam({ sel: null })}
          title={`${selected} — breakdown`}
          maxHeight="88dvh"
          // The side panel above only appears at xl, so the sheet has to cover
          // everything below xl, not below lg.
          hideFrom="xl"
        >
          <DetailPanel
            char={selected}
            signedIn={Boolean(user)}
            activeLevel={level}
            embedded
            onClose={() => setParam({ sel: null })}
            onNavigate={(c) => setParam({ sel: c, focus: c })}
            onChanged={() => setReloadKey((k) => k + 1)}
          />
        </BottomSheet>
      )}

      {/* Filters and search, which below lg have nowhere else to live. */}
      <BottomSheet
        open={controlsOpen}
        onClose={() => setControlsOpen(false)}
        title="Search and filters"
      >
        <div className="flex flex-col gap-3 p-3">
          <MapControls {...controlsProps} />
        </div>
      </BottomSheet>
    </div>
  )
}
